import { useMemo } from 'react';

import { AUTO, supportedZones, useTimezone } from './TimezoneProvider';
import { useFormatters } from './format';

/**
 * Timezone override.
 *
 * `Auto` is first and is the default, so the common case is one click back to following the
 * device. The current offset is shown beside each zone because "Australia/Adelaide" does not
 * tell an analyst whether it is half an hour off Sydney right now.
 */
export function TimezonePicker({ id = 'timezone' }: { id?: string }) {
  const { preference, zone, browserZone, isAuto, setPreference } = useTimezone();
  const { precise } = useFormatters();

  const zones = useMemo(() => {
    const all = supportedZones();
    // The stored preference may name a zone this runtime does not enumerate. Include it so
    // the control never silently displays something other than what is in effect.
    return all.includes(zone) ? all : [zone, ...all];
  }, [zone]);

  const offsetLabel = useMemo(() => {
    const format = (target: string) => {
      try {
        const parts = new Intl.DateTimeFormat('en-AU', {
          timeZone: target,
          timeZoneName: 'shortOffset',
        }).formatToParts(new Date());
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
      } catch {
        return '';
      }
    };
    return format;
  }, []);

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-medium text-body">
        Display timezone
      </label>
      <select
        id={id}
        value={preference}
        onChange={(event) => setPreference(event.target.value)}
        className="w-full max-w-md rounded-lg border border-line-strong bg-sunken px-3 py-2 text-sm text-body hover:border-brand/60 focus-visible:border-brand"
      >
        <option value={AUTO}>
          Auto — follow this device ({browserZone} {offsetLabel(browserZone)})
        </option>
        {zones.map((name) => (
          <option key={name} value={name}>
            {name} {offsetLabel(name)}
          </option>
        ))}
      </select>
      <p className="text-xs text-subtle">
        {isAuto
          ? `Detected from this browser. Timestamps render in ${zone}.`
          : `Pinned to ${zone}. This device reports ${browserZone}.`}{' '}
        Stored in this browser only — it is a display preference and is never sent to the API.
      </p>
      <p className="text-xs text-subtle">
        Now: <span className="tabular-nums text-muted">{precise(new Date().toISOString())}</span>
      </p>
    </div>
  );
}
